// ABOUTME: Provides direct and React Query-backed API request hooks.
// ABOUTME: Supports opt-in cancellation and reactive scoped keys for sensitive queries.
import React, { useState } from "react";
import { apiDirectCall, AxiosType } from "./api";
// import { useQuery, queryCache } from "react-query";
import { useQueryClient, useQuery } from "react-query";
import { getApiCacheQueryKey } from "./authCacheIsolation";

export type ApiStateType = {
    status: "initial" | "fetching" | "success" | "error";
    response: Compleo.IObject;
    exception: any;
    useQueryKey?: any;
};

export type UseApiType = (
    endpoint: string,
    verb?: AxiosType,
    params?: Compleo.IObject,
    executeJustOnCall?: boolean,
    staleTimeMiliseconds?: number,
    keepPreviousData?: boolean,
    alternativeMainKey?: string,
    useQueryParams?: any
) => [ApiStateType, ApiCallType, ApiCallType?, boolean?];

export type ApiCallType = (
    newParams?: Compleo.IObject,
    endpointParam?: string,
    verbParam?: AxiosType
) => Promise<void>;

const genericCall = async ({ address, verb, params, signal }: any) => {
    const { data } = await apiDirectCall(
        address,
        verb,
        params,
        signal ? { signal } : {}
    );
    return data;
};

export const getApiQuery = ({
    address,
    verb,
    params,
    abortOnUnmount = false
}: {
    address: string;
    verb: AxiosType;
    params: Compleo.IObject;
    abortOnUnmount?: boolean;
}) =>
    (context: { signal?: AbortSignal }) =>
        genericCall({
            address,
            verb,
            params,
            signal: abortOnUnmount ? context.signal : undefined
        });

export const useApiCache: UseApiType = (
    endpoint: string,
    verb: AxiosType = "get",
    params = {},
    executeJustOnCall = true,
    staleTimeMiliseconds = 0,
    keepPreviousData: boolean = false,
    alternativeMainKey?: string,
    useQueryParams: any = {}
) => {
    const queryClient = useQueryClient();
    const {
        abortOnUnmount = false,
        queryScope,
        enabled: queryEnabled = true,
        syncQueryArguments = false,
        ...reactQueryParams
    } = useQueryParams || {};

    const [paramsState, setParamsState] = useState({
        endpoint,
        verb,
        params,
        queryScope,
        execute: !executeJustOnCall
    });
    const makeRequest = async (
        newParams = params,
        endpointParam: string = endpoint,
        verbParam: AxiosType = verb
    ) => {
        setParamsState({
            endpoint: endpointParam,
            verb: verbParam,
            params: newParams,
            queryScope,
            execute: true
        });
    };

    const effectiveQueryState = syncQueryArguments
        ? {
              endpoint,
              verb,
              params,
              queryScope,
              execute: paramsState.execute
          }
        : paramsState;

    const parameter = getApiCacheQueryKey({
        mainKey: alternativeMainKey || effectiveQueryState.endpoint,
        address: effectiveQueryState.endpoint,
        verb: effectiveQueryState.verb,
        params: effectiveQueryState.params,
        queryScope: effectiveQueryState.queryScope
    });

    const query = useQuery(
        parameter,
        getApiQuery({
            address: effectiveQueryState.endpoint,
            verb: effectiveQueryState.verb,
            params: effectiveQueryState.params,
            abortOnUnmount
        }),
        {
            staleTime: staleTimeMiliseconds,
            refetchOnWindowFocus: false,
            enabled: effectiveQueryState.execute && queryEnabled,
            keepPreviousData: keepPreviousData,
            ...reactQueryParams
        }
    );

    const invalidateQuery = async (
        newParams = params,
        endpointParam: string = endpoint,
        verbParam: AxiosType = verb
    ) => {
        const invalidationKey =
            params === undefined
                ? getApiCacheQueryKey({
                      mainKey: effectiveQueryState.endpoint,
                      address: effectiveQueryState.endpoint,
                      verb: effectiveQueryState.verb,
                      params: effectiveQueryState.params,
                      queryScope: effectiveQueryState.queryScope
                  })
                : getApiCacheQueryKey({
                      mainKey: endpointParam,
                      address: endpointParam,
                      verb: verbParam,
                      params: newParams,
                      queryScope: effectiveQueryState.queryScope
                  });
        queryClient.invalidateQueries(invalidationKey);
    };

    let status: "initial" | "fetching" | "success" | "error" = "initial";
    switch (query.status) {
        case "success":
        case "error":
            status = query.status;
            break;
        case "loading":
            status = "fetching";
            break;
        default:
            status = "initial";
            break;
    }
    let statusRetorno = status;
    if (effectiveQueryState.execute === false) {
        statusRetorno = "initial";
    }
    const metadadosRetorno: ApiStateType = {
        response: { data: query.data },
        status: statusRetorno,
        exception: query.error,
        useQueryKey: parameter
    };

    return [metadadosRetorno, makeRequest, invalidateQuery, query.isFetching];
};

export type UseApiTypeMutate = (
    endpoint: string,
    verb?: AxiosType,
    params?: Compleo.IObject,
    executeJustOnCall?: boolean
) => [
    ApiStateType,
    (
        newParams?: any,
        newverb?: AxiosType,
        newEndpoint?: string,
        requestOptions?: Compleo.IObject
    ) => Promise<void>
];

export const useApi: UseApiTypeMutate = (
    endpoint: string,
    verb: AxiosType = "get",
    params = {},
    executeJustOnCall: boolean = true
) => {
    const initialState: ApiStateType = {
        status: "initial",
        response: {},
        exception: null,
        useQueryKey: ""
    };

    const [state, setState] = useState(initialState);

    const makeRequest = async (
        newParams: any = params,
        newverb: AxiosType = verb,
        newEndpoint: string = endpoint,
        requestOptions: Compleo.IObject = {}
    ) => {
        setState({ ...state, status: "fetching" });
        try {
            const response = await apiDirectCall(
                newEndpoint,
                newverb,
                newParams,
                requestOptions
            );
            setState({ ...state, response, status: "success" });
            return response;
        } catch (response) {
            setState({ ...state, response: response, status: "error" });
            return response;
        }
    };
    (makeRequest as any)._compleoApiRequest = {
        endpoint,
        verb
    };

    React.useEffect(() => {
        if (!executeJustOnCall) {
            makeRequest();
        }
    }, []);

    return [state, makeRequest];
};

export { apiDirectCall };
